// InviteNewUserFormProps is a discriminated union type
// hence prop destructuring is not available unless a conditional check is being made
// to make sure a specific property exists before using it
/* eslint-disable react/destructuring-assignment */

import { Button, DeprecatedExternalLinkIcon as ExternalLink, Flex, Link, showToast } from '@lokalise/louis';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';

import { useCompleteOnboardingStepMutation } from '@app/api/quickStart';
import { useAddContributorsMutation, useAddContributorsValidateMutation } from '@app/api/team';
import { scheduleToast, showUnexpectedErrorToast } from '@app/components/Toast';
import handleUploadOnboarding from '@app/features/quick-start/utils/handleUploadOnboarding';
import track, { AnalyticsEventName } from '@app/utils/analytics';
import { showNewMessage } from '@app/utils/intercom';
import noop from '@app/utils/noop';

import { ModalFooter } from '../styles';
import type { InviteNewUserProps, InviteNewUserSubmitValues, ReadyToValidateCallback } from '../types';
import { AddContributorsModalPage, ConfirmationType, Role } from '../types';
import {
    getAddButtonLabel,
    getConfirmationType,
    getInviteNewUserFormInitialValue,
    getModalTitle,
    InviteNewUserFormSchema,
} from '../utils';
import AddContributorsConfirmation from './AddContributorsConfirmation';
import InviteNewUserForm from './InviteNewUserForm';
import NoSeatsLeft from './NoSeatsLeft';

const InviteNewUser = (props: InviteNewUserProps) => {
    const {
        planName,
        teamName,
        page,
        seatsLimit,
        seatsTaken,
        invitedSeats,
        gracePeriodSeats,
        isExtraSeatsAllowed,
        isProviderAlpha,
        onModalTitleChange,
        onClose,
    } = props;

    const [confirmationType, setConfirmationType] = useState<ConfirmationType | null>(null);
    const [chargeImmediatelyUsers, setChargeImmediatelyUsers] = useState<string[]>();
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addValidate] = useAddContributorsValidateMutation();
    const [addTeamContributors] = useAddContributorsMutation();
    const [completeStep] = useCompleteOnboardingStepMutation();
    const [exceedingLimit, setExceedingLimit] = useState(false);
    const [formValues, setFormValues] = useState<InviteNewUserSubmitValues>(getInviteNewUserFormInitialValue(props));
    const isNoSeatsLeft = !isProviderAlpha && !isExtraSeatsAllowed && seatsTaken === seatsLimit;

    useEffect(() => {
        onModalTitleChange(getModalTitle(confirmationType ?? page, chargeImmediatelyUsers?.length));
        // Change modal title when confirmationType changes
        // If confirmationType is null, fallback to the page default modal title
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmationType, onModalTitleChange]);

    const getProjectId = (): string => {
        if (page === AddContributorsModalPage.project) {
            return props.activeProject.id;
        }

        return formValues.project?.value ?? '';
    };

    const addContributorsRequest = async () => {
        setIsLoading(true);
        if (formValues) {
            const { contributors, role, isReviewer, referenceLanguages, contributableLanguages, project } = formValues;

            const response = await addTeamContributors({
                emails: contributors,
                projectId: getProjectId(),
                isAdmin: role.value === Role.admin,
                isReviewer,
                referenceLanguages: referenceLanguages?.map(({ value }) => value),
                contributableLanguages: contributableLanguages?.map(({ value }) => value),
            });

            if ('error' in response) {
                showUnexpectedErrorToast();
                setIsSubmitting(false);
                setIsLoading(false);
            } else {
                const eventData = {
                    /* eslint-disable @typescript-eslint/naming-convention */
                    users_quantity: contributors.length,
                    project_name: project?.label,
                    project_id: project?.value,
                    role: role.label,
                    reviewer: isReviewer,
                    reference_languages: referenceLanguages?.map(({ label }) => label).join(),
                    reference_languages_quantity: referenceLanguages?.length,
                    contributable_languages: contributableLanguages?.map(({ label }) => label).join(),
                    contributable_languages_quantity: contributableLanguages?.length,
                    /* eslint-enable @typescript-eslint/naming-convention */
                };
                const eventName =
                    page === AddContributorsModalPage.team
                        ? AnalyticsEventName.NEW_TEAM_MEMBER_ADDED
                        : AnalyticsEventName.NEW_PROJECT_CONTRIBUTOR_ADDED;

                track(eventName, eventData);

                await handleUploadOnboarding(completeStep);

                if (page === AddContributorsModalPage.team) {
                    showToast({
                        type: 'success',
                        title: `Contributor(-s) added successfully`,
                    });
                    setIsLoading(false);
                    onClose();
                } else {
                    scheduleToast({
                        type: 'success',
                        title: `Contributor(-s) added successfully`,
                    });

                    // refresh page without GET params
                    window.location.replace(window.location.pathname);
                }
            }
        }
    };

    const onReadyToValidate = async ({ isReady, form }: ReadyToValidateCallback) => {
        setExceedingLimit(false);
        if (!isReady) {
            setIsSubmitting(false);

            return;
        }

        const validation = await addValidate({ emails: form.contributors });

        if ('error' in validation) {
            showUnexpectedErrorToast();
            setIsSubmitting(false);
        } else {
            if (!validation.data.overlimitAllowed && validation.data.overLimitCount > 0) {
                setIsSubmitting(false);
                setExceedingLimit(true);

                return;
            }

            const confirmation = getConfirmationType(validation.data, isProviderAlpha, seatsLimit, isExtraSeatsAllowed);
            setChargeImmediatelyUsers(validation.data.chargeImmediatelyMembers);
            setConfirmationType(confirmation);
            if (confirmation) {
                setIsSubmitting(false);
            } else {
                await addContributorsRequest();
            }
        }
    };

    const onSubmit = () => (confirmationType ? addContributorsRequest() : setIsSubmitting(true));

    const onChargeImmediatelySeatRemoval = (emailToRemove: string) => {
        const contributors = formValues.contributors.filter((email) => email !== emailToRemove);
        setFormValues({ ...formValues, contributors });
    };

    const renderContent = () => {
        if (isNoSeatsLeft) {
            return <NoSeatsLeft teamName={teamName} />;
        }

        if (confirmationType) {
            const extraSeats =
                gracePeriodSeats + invitedSeats + formValues.contributors.length + seatsTaken - seatsLimit;

            return (
                <AddContributorsConfirmation
                    planName={planName}
                    extraSeats={extraSeats}
                    chargeImmediatelyUsersCount={chargeImmediatelyUsers?.length}
                    confirmationType={confirmationType}
                    chargeImmediatelyMembers={chargeImmediatelyUsers}
                    onChargeImmediatelySeatRemoval={onChargeImmediatelySeatRemoval}
                />
            );
        }

        return (
            <Formik<InviteNewUserSubmitValues>
                initialValues={formValues}
                validationSchema={InviteNewUserFormSchema}
                onSubmit={noop}
                validateOnChange
            >
                <InviteNewUserForm
                    projects={page === AddContributorsModalPage.team ? props.projects : undefined}
                    activeProject={page === AddContributorsModalPage.project ? props.activeProject : undefined}
                    isSubmitting={isSubmitting}
                    onReadyToValidate={onReadyToValidate}
                    onFormChange={setFormValues}
                    exceedingLimit={exceedingLimit}
                />
            </Formik>
        );
    };

    const isUpgradeConfirmation = confirmationType === ConfirmationType.upgradeToEnterprise;
    const confirmationModalLinkLabel = isUpgradeConfirmation ? 'Lokalise plans pricing' : 'Seat pricing';

    return (
        <>
            {renderContent()}
            <ModalFooter justify="space-between" align="center">
                {confirmationType ? (
                    <Link href="https://lokalise.com/pricing" target="_blank" rel="noreferrer">
                        <Flex align="center" gap={1}>
                            {confirmationModalLinkLabel}
                            <ExternalLink />
                        </Flex>
                    </Link>
                ) : (
                    <Link
                        href="https://docs.lokalise.com/translation-workflow/contributors"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <Flex align="center" gap={1}>
                            Learn more about contributors. <ExternalLink />
                        </Flex>
                    </Link>
                )}
                <Flex justify="flex-end" gap={2} grow={1}>
                    {!isUpgradeConfirmation && (
                        <Button
                            variant="secondary"
                            onClick={() => (confirmationType ? setConfirmationType(null) : onClose())}
                        >
                            {confirmationType ? 'Back' : 'Cancel'}
                        </Button>
                    )}
                    {isUpgradeConfirmation && (
                        <>
                            <Button variant="secondary" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={() => showNewMessage()}>
                                Contact sales
                            </Button>
                        </>
                    )}
                    {!isNoSeatsLeft && !isUpgradeConfirmation && (
                        <Button variant="primary" onClick={onSubmit} loading={isSubmitting || isLoading}>
                            {getAddButtonLabel(page, formValues.contributors.length)}
                        </Button>
                    )}
                </Flex>
            </ModalFooter>
        </>
    );
};

export default InviteNewUser;
